<template>
  <div class="template-builder">
    <woot-button
      class="template-builder__close-btn"
      variant="clear"
      type="button"
      color-scheme="secondary"
      icon="dismiss"
      @click="onCloseBuilder"
    />

    <spinner
      v-if="isIframeLoading"
      class="template-builder__loader"
      size="large"
    />
    <iframe
      v-show="!isIframeLoading"
      ref="templateBuilder"
      :src="iframeSrc"
      height="100%"
      width="100%"
      @load="onLoadIframe"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import { hasWhatsAppInbox } from 'shared/mixins/inboxMixin';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import {
  MESSAGE_TEMPLATES_CHANNEL_TYPE,
  TEMPLATE_BUILDER_MODE,
} from '../constants';

export default {
  name: 'MessageTemplateBuilder',
  components: { Spinner },
  props: {
    mode: {
      type: String,
      default: TEMPLATE_BUILDER_MODE.CREATE,
    },
    templateId: {
      type: String,
      default: '',
    },
    templateType: {
      type: String,
      default: MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP,
    },
  },
  data() {
    return {
      isIframeLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isDark: 'theme/isDark',
      inboxes: 'inboxes/getInboxes',
      isSuperAdmin: 'getIsSuperAdmin',
    }),

    currentLanguage() {
      return this.$root.$i18n.locale;
    },

    urlPathByMode() {
      const templateId = this.templateId
        ? '/' + encodeURIComponent(this.templateId)
        : '';
      return `/automation-studio/templates/${this.mode}${templateId}`;
    },

    iframeSrc() {
      const baseUrl =
        process.env.FRONTEND_ENV === 'develop'
          ? process.env.STAGING_FRONTEND_URL
          : window.location.origin;

      const url = new URL(this.urlPathByMode, baseUrl);

      url.searchParams.set('accountId', this.accountId ?? '');
      url.searchParams.set(
        'type',
        this.templateType ?? MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP
      );
      url.searchParams.set('lang', this.currentLanguage ?? 'en');

      return url.toString();
    },
  },
  watch: {
    isDark: {
      handler(val) {
        const iframeDocument = this.$refs.templateBuilder?.contentDocument;
        setColorTheme(val, iframeDocument);
      },
      immediate: true,
    },
  },
  async mounted() {
    window.addEventListener('message', this.onReceiveMessage);

    // Mirror the sidebar rule at page level — without a WhatsApp inbox, the
    // WhatsApp template builder is superadmin-only.
    if (
      this.templateType === MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP &&
      !this.isSuperAdmin
    ) {
      await this.$store.dispatch('inboxes/get');
      if (!hasWhatsAppInbox(this.inboxes)) {
        this.$router.replace({ name: 'home' });
      }
    }
  },
  destroyed() {
    window.removeEventListener('message', this.onReceiveMessage);
  },
  methods: {
    onCloseBuilder() {
      this.$router.push({
        name: `${this.templateType}_message_templates`,
      });
    },
    async onLoadIframe() {
      this.isIframeLoading = false;
    },
    onReceiveMessage({ data }) {
      if (data.event === 'publish-all') {
        this.onCloseBuilder();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.template-builder {
  @apply flex h-full w-full flex-col;
  &__close-btn {
    @apply absolute right-3 top-3;
  }
  &__loader {
    @apply absolute left-0 top-0 z-10 flex h-full w-full justify-center pt-8 dark:bg-gray-900;
  }
}
</style>
